import { TranslateService } from '@ngx-translate/core';

/** A destination the header can link to. */
export interface HeaderNavLink {
  id: string;
  /** i18n key. The header resolves it with the translate pipe, so the label follows the active language. */
  labelKey: string;
  /** PrimeIcons class, shown next to the label in the Tickets panel, the profile menu and the mobile drawer. */
  icon: string;
  link: string;
  /**
   * Further URL prefixes that also mark this link as active, for pages that belong to it but do not live under
   * its own path (e.g. the ticket detail pages, `/PKOFF/:code`).
   */
  alsoActiveFor?: readonly string[];
}

/**
 * A top-level entry that opens a panel of links. Its children are plain links rather than groups, so the
 * hierarchy is one level deep by construction (no cascading fly-outs).
 */
export interface HeaderNavGroup {
  id: string;
  labelKey: string;
  children: readonly HeaderNavLink[];
}

export type HeaderNavItem = HeaderNavLink | HeaderNavGroup;

export const isHeaderNavGroup = (item: HeaderNavItem): item is HeaderNavGroup => 'children' in item;

/**
 * Budget that keeps the header on one line down to the laptop tier (see the width notes in
 * app-header.component.scss). Every top-level entry costs ~90-100px, and the tightest tier has roughly one entry
 * of slack, so a new destination belongs in a group (or in the account menu), not at the top level. A group panel
 * is a single column up to `maxGroupChildren`; beyond that it needs a two-column panel, which is a design
 * decision. Enforced by header-menu.spec.ts.
 */
export const HEADER_NAV_LIMITS = { maxTopLevel: 5, maxGroupChildren: 7 } as const;

/**
 * The primary navigation, shown in the header bar (and, in the same order, in the mobile drawer).
 *
 * This is the site's main sections. Account destinations (Vehicles, ...) are not in here: they live in
 * `HEADER_ACCOUNT_NAV` (account-menu.ts), which is rendered inside the profile menu.
 *
 * To add a destination to an existing group: add a `HeaderNavLink` to that group's `children`, add its
 * `MENUS.*` label to en.json and ar.json, and make sure the route exists. Nothing else in the header changes.
 */
export const HEADER_NAV: readonly HeaderNavItem[] = [
  { id: 'home', labelKey: 'MENUS.HOME', icon: 'pi pi-home', link: '/home' },
  { id: 'violations', labelKey: 'MENUS.VIOLATIONS', icon: 'pi pi-exclamation-triangle', link: '/violations' },
  { id: 'bookings', labelKey: 'MENUS.BOOKINGS', icon: 'pi pi-calendar', link: '/bookings' },
  { id: 'subscriptions', labelKey: 'MENUS.SUBSCRIPTIONS', icon: 'pi pi-list', link: '/subscriptions' },
  {
    id: 'tickets',
    labelKey: 'MENUS.TICKETS',
    children: [
      {
        id: 'parking-tickets',
        labelKey: 'MENUS.PARKING_TICKETS',
        icon: 'pi pi-ticket',
        link: '/parking-tickets',
        // A signed-in user opens a ticket from the list at /PKOFF/:code; that page is still "Parking Tickets".
        alsoActiveFor: ['/PKOFF']
      },
      {
        id: 'valet-tickets',
        labelKey: 'MENUS.VALET_TICKETS',
        icon: 'pi pi-key',
        link: '/valet-tickets',
        // /valet-tickets/:id is already under the link's own path; the public guest link is /VLT/:token.
        alsoActiveFor: ['/VLT']
      }
    ]
  }
];

/** Every link in display order, groups expanded in place. */
export const flattenHeaderNav = (items: readonly HeaderNavItem[] = HEADER_NAV): HeaderNavLink[] =>
  items.flatMap((item) => (isHeaderNavGroup(item) ? item.children : [item]));

const pathOf = (url: string): string => {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  return path === '' ? '/' : path;
};

/** True when `url` is `prefix` itself or anything beneath it (segment-aware: `/vehicles-x` is not under `/vehicles`). */
export const isUnderRoute = (url: string, prefix: string): boolean => {
  const path = pathOf(url);
  const base = pathOf(prefix);
  return path === base || path.startsWith(base + '/');
};

/** Whether a link is the current page (or a page that belongs to it). Query string and fragment are ignored. */
export const isNavLinkActive = (url: string, link: HeaderNavLink): boolean =>
  [link.link, ...(link.alsoActiveFor ?? [])].some((prefix) => isUnderRoute(url, prefix));

/** A group is active when any of its children is; a plain link as above. */
export const isNavItemActive = (url: string, item: HeaderNavItem): boolean =>
  isHeaderNavGroup(item) ? item.children.some((child) => isNavLinkActive(url, child)) : isNavLinkActive(url, item);

/**
 * Flat, sidebar-shaped view of the header menu. Kept only because the legacy sidebar menu
 * (views/layout/sidebar/menu.ts) still calls it; the header itself renders `HEADER_NAV` directly.
 */
export const getHeaderMenu = (translate?: TranslateService) => {
  return flattenHeaderNav().map((item, index) => ({
    id: index + 1,
    label: translate?.instant(item.labelKey),
    isHidden: false,
    icon: item.icon,
    expanded: false,
    link: item.link
  }));
};
